import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { useHandleDismiss } from '@post-office/message-templates-common--growth-starburst/hooks/use-handle-dismiss';
import { TwcSampleBanner } from '@atlassian/twc-sample-banner';

import { PersonalizedBanner } from '../../../components/personalized-banner';
import { type MessageTemplateContext } from '@post-office/message-template--twc-sidenav-banner';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { getOnboardingUrl, resolveRedirectTo } from '../../../utils/get-onboarding-url';
import {
	type ExperimentArm,
	getMissingAppExperimentArm,
	isMissingAppFlow,
} from '../../../utils/missing-app-experiment';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
	redirectTo: 'jira' | 'confluence';
	arm: ExperimentArm;
};

const DISMISS_CLICK_IDENTIFIER: Record<ExperimentArm, string> = {
	personalized: 'twc-sidenav-banner-personalized-dismiss',
	generic: 'twc-sidenav-banner-generic-dismiss',
	control: 'twc-sidenav-banner-dismiss',
};

// Custom backend that fetches TCS hostname and TAP site trait to build the Confluence onboarding URL
export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			goalMetadata: { primaryGoal: { goalId: 'clickthrough' } },
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({ message, request }) => {
			const { logger, orchestrationContext, query, statsig } = request;
			const { messageInstanceId, messageTemplateId, triggerId } = message;
			const context = message.context as MessageTemplateContext;
			const { cloudId: tenantId, orgId, twcEntitlementId } = context;
			const { locale } = orchestrationContext;
			const redirectTo = resolveRedirectTo(context.redirectTo);
			const errorCounter = (reason: string) =>
				request.metrics.counter('twc-sidenav-banner.conf.error.count', { reason }).incr();

			if (!tenantId || typeof tenantId !== 'string') {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Tenant ID not available',
				);
				errorCounter('tenant-id-not-available');
				throw new Error('Tenant ID not available');
			}

			const commonOnboardingParams = {
				redirectTo,
				requestContext: request,
				messageInstanceId,
				messageTemplateId,
				orgId,
				twcEntitlementId,
				triggerId: triggerId as string | undefined,
			};

			// ─── Missing-app personalized flow ─────────────────────────────────
			if (isMissingAppFlow(context)) {
				const { recommendedApp } = context;
				const arm = getMissingAppExperimentArm(statsig, tenantId);

				if (arm === 'control') {
					logger.info(
						{ messageInstanceId, messageTemplateId, triggerId, arm },
						'Missing-app experiment control arm - abort hydration',
					);
					errorCounter('missing-app-control');
					throw new Error('Missing-app experiment control arm');
				}

				// flow: 'missing-app' → confluence routes to the Rovo create surface,
				// jira routes through the tenant-aware GUPS helper.
				const onboardingUrl = await getOnboardingUrl({
					...commonOnboardingParams,
					flow: 'missing-app',
				});

				if (!onboardingUrl) {
					logger.error(
						{ messageInstanceId, messageTemplateId, triggerId, recommendedApp },
						'No onboarding URL found (missing-app)',
					);
					errorCounter('no-onboarding-url-missing-app');
					throw new Error('No onboarding URL found (missing-app)');
				}

				request.metrics
					.counter('twc-sidenav-banner.conf.success.count', { variant: 'missing-app', arm })
					.incr();

				return {
					...context,
					redirectTo,
					onboardingUrl,
					messageInstanceId,
					locale,
					arm,
				};
			}

			// ─── Generic TWCG-991 flow ─────────────────────────────────────────
			const isEnabled = statsig.getExperimentValue(
				{ identifiers: { tenantId } },
				{ name: 'twcg-991-jtbd-onboarding', parameter: 'isEnabled', defaultValue: false },
			);
			if (!isEnabled) {
				logger.info(
					{ messageInstanceId, messageTemplateId, triggerId, tenantId },
					'Control cohort - abort hydration',
				);
				errorCounter('control-cohort');
				throw Error('Control cohort - abort hydration');
			}

			logger.info(
				{ messageInstanceId, messageTemplateId, triggerId, orchestrationContext, query },
				'Hydrating twc-sidenav-banner (in-app-v2/confluence-side-nav)',
			);

			// flow: 'generic' → confluence routes through TCS/TAP-resolved cloudId.
			const onboardingUrl = await getOnboardingUrl({
				...commonOnboardingParams,
				flow: 'generic',
			});

			if (!onboardingUrl) {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId, redirectTo },
					'No onboarding URL found',
				);
				errorCounter('no-onboarding-url');
				throw new Error('No onboarding URL found');
			}

			request.metrics.counter('twc-sidenav-banner.conf.success.count').incr();

			return {
				...context,
				redirectTo,
				onboardingUrl,
				messageInstanceId,
				locale,
				arm: 'generic' satisfies ExperimentArm,
			};
		},
	},
}));

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			redirectTo,
			handleOnDismissed,
			onboardingUrl,
			messageInstanceId,
			locale,
			arm,
			recommendedApp,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
		}: MessageFrontendProps) => {
			const { handleDismiss } = useHandleDismiss({
				onDismissed: handleOnDismissed,
				clickIdentifier: DISMISS_CLICK_IDENTIFIER[arm],
			});

			if (arm === 'personalized' && recommendedApp) {
				return (
					<PersonalizedBanner
						locale={locale}
						recommendedApp={recommendedApp}
						onClose={handleDismiss}
						onCtaClick={handleDismiss}
						href={onboardingUrl}
						messageInstanceId={messageInstanceId}
						actionTier={actionTier}
						targetTenantId={targetTenantId}
						collectionEntitlementId={collectionEntitlementId}
					/>
				);
			}

			return (
				<TwcSampleBanner
					locale={locale}
					product={redirectTo}
					onClose={handleDismiss}
					onTryNowClick={handleDismiss}
					href={onboardingUrl}
					messageInstanceId={messageInstanceId}
				/>
			);
		},
	},
});
